import { useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/Badge'
import { Button, ButtonLink } from '@/components/ui/Button'
import { Checkbox, Field, Select, Textarea } from '@/components/ui/Field'
import { DataList, DataRow, Page, PageHeader, Section } from '@/components/ui/Page'
import { ErrorState, Loading, Notice } from '@/components/ui/States'
import { formatAgo, formatDateLong, formatInstant } from '@/lib/dates'
import { formatCouple, formatDistance, formatPhone } from '@/lib/format'
import { describeError } from '@/lib/supabase'
import { CHANNEL_LABEL, SOURCE_LABEL } from '@/features/marketing/queries'
import { VenuePicker } from '@/features/venues/VenuePicker'
import { STATUS_LABEL, STATUS_TONE } from './EnquiriesPage'
import { useBookingTick, useConvertInquiry, useInquiry, useUpdateInquiry, type InquiryStatus, type InquiryWithRelations } from './queries'

/**
 * One enquiry, and the two decisions it needs: what to say back, and whether it
 * becomes a wedding.
 *
 * Matching the venue to the library is offered here rather than later, because
 * that is what gives the quote a distance — and doing it while the enquiry is
 * open is the only moment anyone will remember to.
 */

const STATUSES: InquiryStatus[] = ['new', 'reviewing', 'quoted', 'booked', 'lost', 'spam']

export function EnquiryDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const query = useInquiry(id)
  const update = useUpdateInquiry(id ?? '')
  const convert = useConvertInquiry()

  const [lostReason, setLostReason] = useState('')

  if (query.isPending) return <Loading label="Loading the enquiry" />

  if (query.isError || !query.data) {
    return (
      <Page>
        <ErrorState
          title="That enquiry could not load"
          detail={describeError(query.error)}
          onRetry={() => void query.refetch()}
        />
      </Page>
    )
  }

  const inquiry = query.data
  const couple = formatCouple(
    { first_name: inquiry.first_name, last_name: inquiry.last_name },
    inquiry.partner_first_name ? { first_name: inquiry.partner_first_name, last_name: inquiry.partner_last_name } : null,
  )

  async function convertToWedding() {
    if (!id) return
    const jobId = await convert.mutateAsync(id)
    toast.success('Wedding created')
    void navigate(`/weddings/${jobId}`)
  }

  const canConvert = !inquiry.job && inquiry.event_date !== null && inquiry.status !== 'spam'

  return (
    <Page>
      <PageHeader
        eyebrow="Enquiry"
        title={<span className="display">{couple}</span>}
        meta={
          <>
            <span>Received {formatAgo(inquiry.received_at)}</span>
            <Badge tone={STATUS_TONE[inquiry.status]}>{STATUS_LABEL[inquiry.status]}</Badge>
            {inquiry.responded_at ? <span>Replied {formatAgo(inquiry.responded_at)}</span> : null}
          </>
        }
        actions={
          <>
            <ButtonLink to="/projects" variant="ghost">
              Back to projects
            </ButtonLink>
            {inquiry.job ? (
              <ButtonLink to={`/weddings/${inquiry.job.id}`} variant="primary">
                Open the wedding
              </ButtonLink>
            ) : canConvert ? (
              <Button
                variant="primary"
                loading={convert.isPending}
                loadingLabel="Creating"
                onClick={() => void convertToWedding()}
              >
                Make it a wedding
              </Button>
            ) : null}
          </>
        }
      />

      {inquiry.archived_at ? (
        <Notice
          className="mb-4"
          tone="neutral"
          title="Archived"
          action={
            <Button size="sm" onClick={() => update.mutate({ archived_at: null })}>
              Restore
            </Button>
          }
        >
          Archived {formatAgo(inquiry.archived_at)}. Nothing is ever deleted here.
        </Notice>
      ) : null}

      {!inquiry.job && inquiry.event_date === null && inquiry.status !== 'spam' ? (
        <Notice className="mb-4" tone="amber" title="No wedding date">
          A wedding needs a date before it can go on the board. Ask, then add it below.
        </Notice>
      ) : null}

      <div className="grid gap-x-10 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div>
          <Section title="What they sent">
            {inquiry.message ? (
              <blockquote className="measure border-l-2 border-l-[var(--color-rule-strong)] pl-4 text-[length:var(--text-base)] leading-relaxed whitespace-pre-wrap">
                {inquiry.message}
              </blockquote>
            ) : (
              <p className="text-[length:var(--text-sm)] text-taupe">No message came with this enquiry.</p>
            )}
          </Section>

          <Section
            title="Venue"
            description="Matching this to your venue library is what gives the quote a travel distance."
          >
            {inquiry.venue_text ? (
              <p className="mb-3 text-[length:var(--text-sm)]">
                <span className="label mr-2">As typed</span>
                {inquiry.venue_text}
              </p>
            ) : null}

            <VenuePicker
              value={inquiry.venue_id}
              onChange={(venueId) => update.mutate({ venue_id: venueId })}
              suggestedName={inquiry.venue_text}
            />

            {inquiry.venue ? (
              <DataList className="mt-4 max-w-sm">
                <DataRow label="Venue">{inquiry.venue.name}</DataRow>
                <DataRow label="Town">{inquiry.venue.town ?? '—'}</DataRow>
                <DataRow label="One-way distance">
                  {inquiry.venue.distance_km === null ? (
                    <Link to="/settings/venues" className="text-clay underline decoration-clay/40 underline-offset-4">
                      Not measured yet
                    </Link>
                  ) : (
                    formatDistance(Number(inquiry.venue.distance_km))
                  )}
                </DataRow>
              </DataList>
            ) : null}
          </Section>

          <Booking inquiry={inquiry} />

          <Section title="Status">
            <div className="flex max-w-md flex-col gap-4">
              <Field label="Where this enquiry stands">
                <Select
                  value={inquiry.status}
                  onChange={(event) => update.mutate({ status: event.target.value as InquiryStatus })}
                >
                  {STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {STATUS_LABEL[status]}
                    </option>
                  ))}
                </Select>
              </Field>

              {inquiry.status === 'lost' ? (
                <Field label="Why it was lost" hint="Worth a line — patterns show up over a season.">
                  <Textarea
                    rows={2}
                    defaultValue={inquiry.lost_reason ?? ''}
                    onChange={(event) => setLostReason(event.target.value)}
                    onBlur={() => {
                      if (lostReason !== (inquiry.lost_reason ?? '')) update.mutate({ lost_reason: lostReason })
                    }}
                  />
                </Field>
              ) : null}

              {!inquiry.archived_at ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="self-start"
                  onClick={() => update.mutate({ archived_at: new Date().toISOString() })}
                >
                  Archive this enquiry
                </Button>
              ) : null}
            </div>
          </Section>
        </div>

        <aside className="lg:border-l lg:pl-8">
          <Section title="Details" className="border-t-0 pt-0 lg:pt-5">
            <DataList>
              <DataRow label="Email">
                <a href={`mailto:${inquiry.email}`} className="underline decoration-[var(--color-rule-strong)] underline-offset-4">
                  {inquiry.email}
                </a>
              </DataRow>
              <DataRow label="Phone">
                {inquiry.phone ? (
                  <a href={`tel:${inquiry.phone}`} className="underline decoration-[var(--color-rule-strong)] underline-offset-4">
                    {formatPhone(inquiry.phone)}
                  </a>
                ) : (
                  '—'
                )}
              </DataRow>
              <DataRow label="Wedding date">
                {inquiry.event_date ? formatDateLong(inquiry.event_date) : 'Not given'}
              </DataRow>
              <DataRow label="Guests">{inquiry.guest_count ?? '—'}</DataRow>
              <DataRow label="Wants">
                {inquiry.services === 'both'
                  ? 'Photo + film'
                  : inquiry.services === 'photography'
                    ? 'Photography'
                    : inquiry.services === 'videography'
                      ? 'Film'
                      : '—'}
              </DataRow>
              <DataRow label="Heard about you">{inquiry.heard_about ?? '—'}</DataRow>
              <DataRow label="Came in by">
                <Select
                  aria-label="How the enquiry came in"
                  value={inquiry.channel ?? ''}
                  onChange={(event) => update.mutate({ channel: event.target.value || null })}
                  className="h-8 py-0"
                >
                  <option value="">Not recorded</option>
                  {Object.entries(CHANNEL_LABEL).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </Select>
              </DataRow>
              <DataRow label="What brought them">
                <Select
                  aria-label="What brought them"
                  value={inquiry.lead_source ?? ''}
                  onChange={(event) => update.mutate({ lead_source: event.target.value || null })}
                  className="h-8 py-0"
                >
                  <option value="">Not recorded</option>
                  {Object.entries(SOURCE_LABEL).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </Select>
              </DataRow>
              <DataRow label="Received">{formatInstant(inquiry.received_at)}</DataRow>
            </DataList>
          </Section>
        </aside>
      </div>
    </Page>
  )
}

/** Contract signed and deposit received: both ticked, it moves to Booked. */
function Booking({ inquiry }: { inquiry: InquiryWithRelations }) {
  const tick = useBookingTick(inquiry)
  const job = inquiry.job
  const noDate = !job && inquiry.event_date === null

  return (
    <Section title="Booking" description="Tick both and it moves to Booked on the projects board.">
      <div className="flex flex-col gap-3">
        <Checkbox
          label="Contract signed"
          checked={Boolean(job?.contract_signed_at)}
          disabled={tick.isPending || noDate}
          onChange={(event) => tick.mutate({ contract_signed_at: event.target.checked ? new Date().toISOString() : null })}
        />
        <Checkbox
          label="Deposit received"
          checked={job?.deposit_status === 'paid' || job?.deposit_status === 'waived'}
          disabled={tick.isPending || noDate}
          onChange={(event) => tick.mutate({ deposit_status: event.target.checked ? 'paid' : 'pending' })}
        />
        {noDate ? (
          <p className="text-[length:var(--text-2xs)] text-taupe">Add the wedding date first.</p>
        ) : job?.booked_at ? (
          <p className="text-[length:var(--text-2xs)] text-sage">
            Booked. <Link to={`/weddings/${job.id}`} className="underline underline-offset-4">Open the wedding</Link>
          </p>
        ) : null}
      </div>
    </Section>
  )
}
